import React from 'react';
import { nameNow } from '../workflow/constants';

// "<snapshot> (now: <live>)" — docs/data-model.md §2.
//
// A record names its client as of the day it was written and is never
// rewritten when the client master is renamed. The API sends the live value
// as a `*Now` field ONLY when it differs, and this is the one way to print it:
// the snapshot stays the body, the current name follows in muted brackets.
export const NOW_TITLE = 'The client master has been renamed since this record was written';

export const NowName = ({ value, now, fallback = '—', className = '' }) => {
  if (!value && !now) return <span className={className}>{fallback}</span>;
  return (
    <span className={`break-words ${className}`}>
      {value || fallback}
      {now && <span className={nameNow} title={NOW_TITLE}>(now: {now})</span>}
    </span>
  );
};

// Plain-string form for places that cannot render a node (option labels,
// tooltips, document titles).
export const nowLabel = (value, now) => (now ? `${value} (now: ${now})` : value);

// The name to GROUP, FILTER, SORT or EXPORT a person by: the current one.
// Snapshots are what a record says, but they are not an identity — a renamed
// person holds two different strings across their older and newer records, so
// keying anything on the snapshot splits one person into two (a partner counted
// twice in a summary, listed twice in a filter). Printing is the opposite case:
// there the snapshot leads and `NowName` adds the rename after it.
export const staffNow = (value, now) => now || value || null;

// A list of staff names — an engagement's or a request's leads / team members.
// Staff names are snapshots too (docs/data-model.md §2), so each name carries
// its own rename: [{staffId, staffName, staffNameNow}]. This is the one way to
// print such a list, so every surface says it the same way.
export const StaffNames = ({ list, fallback = '—', className = '' }) => {
  const people = (list || []).filter((m) => m && m.staffName);
  if (people.length === 0) return <span className={className}>{fallback}</span>;
  return (
    <span className={className}>
      {people.map((m, i) => (
        <span key={m.staffId ?? m.staffName}>
          {i > 0 && ', '}
          <NowName value={m.staffName} now={m.staffNameNow} />
        </span>
      ))}
    </span>
  );
};

export default NowName;
